import type { FC, FormEvent, MouseEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { Drawer } from '../../game/drawer/Drawer';
import { GameButton } from '../../game/gameButton/gameButton';
import { L12nText } from '../../text/L12nText';
import { GameRadioGroup } from '../gameRadioGroup/GameRadioGroup';

import styles from './AutoPlay.module.css';

export interface AutoPlayViewProps {
  totalBet: ReactNode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => boolean;
  onStop: (event: MouseEvent<HTMLButtonElement>) => void;
  toggleOpen: (event?: MouseEvent<HTMLElement>) => void;
  open: boolean;
  isInProgress: boolean;
  currentAmount: number;
  startEnabled: boolean;
  className?: string;
  limitComponent?: ReactNode;
}

export const AutoPlayView: FC<AutoPlayViewProps> = ({
  totalBet,
  onSubmit,
  onStop,
  toggleOpen,
  currentAmount,
  isInProgress,
  startEnabled,
  limitComponent,
  open,
}) => {
  const autoplayOptions = ['5', '10', '25', '50', '100']; // FIXME: I should come from server
  const [selectedAmount, setSelectedAmount] = useState(autoplayOptions[0]);
  const [inProgressState, setProgressState] = useState(false);

  useEffect(() => {
    if (open) {
      setProgressState(isInProgress);
    }
  }, [isInProgress, open]);

  const modifyDisabled = currentAmount === parseInt(selectedAmount, 10) || !startEnabled;

  return (
    <Drawer data-testid="autoplay-drawer" open={open} onClose={toggleOpen} end title={<L12nText tk="AUTOPLAY" />}>
      <form onSubmit={onSubmit}>
        <div className={styles.autoplay}>
          {limitComponent ? <div className={styles.autoplay__limits}>{limitComponent}</div> : null}
          <L12nText tk="AUTOPLAYS_NUMBER" />
          <GameRadioGroup
            value={selectedAmount}
            name="amountValue"
            onChange={(val): void => {
              setSelectedAmount(val);
            }}
            className={styles.autoplay_options}
            options={autoplayOptions.map((el) => ({ label: <>{el}</>, value: el }))}
          />
          <L12nText tk="BET_PER_ROUND" /> <b className={startEnabled ? '' : styles.autoplay__bet_error}>{totalBet}</b>
          <div className={styles.autoplay_controls}>
            {inProgressState ? (
              <>
                <GameButton
                  data-testid="autoplay-stop-button"
                  label={<L12nText tk="STOP" />}
                  active={false}
                  grow
                  style={{ marginBottom: 8 }}
                  type="button"
                  onClick={onStop}
                  className={styles.autoplay__button}
                />
                <GameButton
                  data-testid="autoplay-modify-button"
                  label={<L12nText tk="MODIFY" />}
                  active={true}
                  grow
                  type="submit"
                  disabled={modifyDisabled}
                  className={styles.autoplay__button}
                />
              </>
            ) : (
              <GameButton
                data-testid="autoplay-start-button"
                label={<L12nText tk="AUTOPLAY_START" />}
                active={true}
                grow
                type="submit"
                disabled={!startEnabled}
                className={styles.autoplay__button}
              />
            )}
          </div>
        </div>
      </form>
    </Drawer>
  );
};
